
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import { get, post } from '@/services/http';
import { StoreModel } from '@/models/store';
import ActionButton from '@/components/ActionButton.vue';
import Alert from '@/components/Alert.vue';
import {EventBus} from '@/main';
import { isEmbedded } from '@/utils/shopifyBridge';

// Declare global objects
declare const gapi: any;
declare const google: any;

@Component({
  components: {
    ActionButton,
    Alert,
  },
})
export default class GoogleDriveSelector extends Vue {
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  public success: boolean = false;
  public error: boolean = false;
  public loading: boolean = false;
  public snackbar: boolean = false;
  public snackbarTimeout: number = 3000;
  public snackbarText: string = '';

  @Prop({ default: '' })
  public initSelectedAccount!: string;
  @Prop({ default: '' })
  public initSelectedDriveFolder!: string;

  // New props for file selection functionality
  @Prop({ default: 'folder' }) // 'folder' or 'file'
  public mode!: string;

  @Prop({ default: () => [] })
  public allowedMimeTypes!: string[];

  @Prop({ type: Boolean, default: false })
  public enableFileAttachments!: boolean;

  // Opt-in to show the folder picker in file mode (uploads land in the selected folder)
  @Prop({ type: Boolean, default: false })
  public enableFolderSelection!: boolean;

  @Prop({ default: () => [] })
  public attachments!: any[];

  public accounts: any[] = [];
  public selectedAccount: string = '';
  public selectedDriveFolder: string = '';
  public selectedDriveFolderName: string = '';
  private pickerApiLoaded: boolean = false;

  // New data properties for file selection/upload
  public selectedFiles: any[] = [];
  public selectedFilesMeta: any[] = [];
  public uploading = false;
  private readonly DEFAULT_ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.presentation'
  ];


  public async created() {
    this.loading = true;
    if (this.initSelectedAccount) {
      this.selectedAccount = this.initSelectedAccount;
    }
    if (this.initSelectedDriveFolder) {
      this.selectedDriveFolder = this.initSelectedDriveFolder;
    }
    await this.getAccounts();
    await this.handleClientLoad();

    this.loading = false;
    this.success = true;
  }

  public async getAccounts() {
    const apiUrl = `/token/google_oauth`;
    const response: any = await get(apiUrl, {});
    if (response.hasOwnProperty('data')) {
      const options_ = [];
      for (const item of response.data) {
        options_.push({
          text: item.email,
          value: item.email,
        });
      }
      this.accounts = options_;
      if (!this.selectedAccount && options_.length > 0) {
        this.selectedAccount = options_[0]['value'];
      }
    }
  }

  public async remove(item: any) {
    this.selectedAccount = ''; // You can adjust this logic based on your use case
    this.accounts = this.accounts.filter(i => i.value !== item.value);
    const emailToRemove = item.value;
    const apiUrl = `/token/google_oauth/revoke`;
    try {
      const response: any = await post(apiUrl, {email: emailToRemove});
    } catch (e) {
      // console.log(e);
    }
  };

  public async selectFolderDrive() {
    if (!this.selectedAccount) {
      return;
    }
    const response: any = await get('/token/google_oauth/access_token', {email: this.selectedAccount});
    const oauthToken = response.data.access_token;

    if (this.pickerApiLoaded && oauthToken) {
      let origin = null;
      if (isEmbedded()) {
        origin = 'https://admin.shopify.com';
      } else {
        origin = `${process.env.VUE_APP_HOST}`;
      }

      const picker = new google.picker.PickerBuilder()
          .addView(new google.picker.DocsView(google.picker.ViewId.FOLDERS).setParent('root').setSelectFolderEnabled(true))
          .setSelectableMimeTypes('application/vnd.google-apps.folder')
          .setOAuthToken(oauthToken)
          .setCallback(this.pickerDriveCallback)
          .setOrigin(origin)
          .build();
      picker.setVisible(true);
    }
  }

  public async connectGoogleAccount() {
    sessionStorage.setItem('current_full_path', this.$route.fullPath);
    EventBus.$emit('redirect-to-google-auth');
  }

  private handleClientLoad() {
    gapi.load('picker', { callback: () => {
        this.pickerApiLoaded = true;
      }});
  }

  private pickerDriveCallback(data: any) {
    if (data.action === google.picker.Action.PICKED) {
      const selectedFile = data.docs[0];
      const folderId = selectedFile.id;
      const folderName = selectedFile.name;
      this.selectedDriveFolder = folderId;
      this.selectedDriveFolderName = folderName;
    }
  }

  // New method for file upload
  public triggerFileUpload() {
    if (!this.selectedAccount) {
      this.$emit('show-message', 'Please select a Google account first', 'warning');
      return;
    }

    // Trigger the hidden file input
    const fileInput = this.$refs.fileInput as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle file selection from input
  public async handleFileSelection(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files;

    if (!files || files.length === 0) {
      return;
    }

    // Filter files by allowed MIME types
    const allowedTypes = this.allowedMimeTypes.length > 0 ? this.allowedMimeTypes : this.DEFAULT_ALLOWED_MIME_TYPES;
    const validFiles: File[] = [];

    for (const file of Array.from(files)) {
      if (allowedTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        this.$emit('show-message', `File type ${file.type} is not allowed for ${file.name}`, 'warning');
      }
    }

    if (validFiles.length === 0) {
      return;
    }

    // Upload files to Google Drive
    await this.uploadFilesToDrive(validFiles);

    // Clear the input
    target.value = '';
  }

  // Upload files to Google Drive
  private async uploadFilesToDrive(files: File[]) {
    if (!this.selectedAccount) return;

    try {
      // Get access token
      const response: any = await get('/token/google_oauth/access_token', {email: this.selectedAccount});
      const accessToken = response.data.access_token;

      if (!accessToken) {
        this.$emit('show-message', 'Failed to get access token', 'error');
        return;
      }

      const uploadedAttachments = [];
      this.uploading = true;

      for (const file of files) {
        try {
          const uploadedFile = await this.uploadSingleFile(file, accessToken);
          if (uploadedFile) {
            uploadedAttachments.push(uploadedFile);
          }
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          this.$emit('show-message', `Failed to upload ${file.name}`, 'error');
        }
      }

      if (uploadedAttachments.length > 0) {
        this.selectedFilesMeta = uploadedAttachments;
        this.$emit('filesSelected', uploadedAttachments);
      }

    } catch (error) {
      console.error('Failed to upload files:', error);
      this.$emit('show-message', 'Failed to upload files to Google Drive', 'error');
    } finally {
      this.uploading = false;
    }
  }

  // Upload a single file to Google Drive
  private async uploadSingleFile(file: File, accessToken: string): Promise<any> {
    // Create metadata for the file
    const metadata = {
      name: file.name,
      parents: this.selectedDriveFolder ? [this.selectedDriveFolder] : [] // Upload to selected folder, or root if none selected
    };

    // Create form data for multipart upload
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    // Upload to Google Drive API
    const uploadResponse = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: form
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.statusText}`);
    }

    const uploadResult = await uploadResponse.json();

    // Make the file shareable (anyone with link can view)
    await fetch(`https://www.googleapis.com/drive/v3/files/${uploadResult.id}/permissions`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        role: 'reader',
        type: 'anyone'
      })
    });

    // Get the shareable link
    const fileResponse = await fetch(`https://www.googleapis.com/drive/v3/files/${uploadResult.id}?fields=id,name,mimeType,webViewLink`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });

    const fileData = await fileResponse.json();

    // Return formatted attachment data
    return {
      filename: fileData.name,
      googleDriveFileId: fileData.id,
      googleDriveLink: fileData.webViewLink,
      fileType: fileData.mimeType,
      uploadedAt: new Date().toISOString(),
      uploadedBy: this.selectedAccount
    };
  }

  // Utility methods for file type handling
  public getFileTypeIcon(mimeType: string): string {
    if (mimeType.includes('pdf')) return 'mdi-file-pdf-box';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'mdi-file-excel-box';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'mdi-file-word-box';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'mdi-file-powerpoint-box';
    if (mimeType.includes('csv')) return 'mdi-file-delimited-outline';
    return 'mdi-file-document-outline';
  }

  public getFileTypeColor(mimeType: string): string {
    if (mimeType.includes('pdf')) return 'red';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'green';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'blue';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'orange';
    if (mimeType.includes('csv')) return 'teal';
    return 'grey';
  }

  public formatFileType(mimeType: string): string {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Excel';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Word';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'PowerPoint';
    if (mimeType.includes('csv')) return 'CSV';
    return 'Document';
  }

  // Get MIME types for file input accept attribute
  public getAllowedMimeTypesForInput(): string {
    const mimeTypes = this.allowedMimeTypes.length > 0 ? this.allowedMimeTypes : this.DEFAULT_ALLOWED_MIME_TYPES;
    return mimeTypes.join(',');
  }

  @Watch('selectedAccount', { immediate: true, deep: true  })
  private async onSelectedAccountChanged(newVal: any, oldVal: any) {
    // if (!this.selectedAccount) { return; }
    this.$emit('updateSelectedAccount', this.selectedAccount);
  }

  @Watch('selectedDriveFolder', { immediate: true, deep: true  })
  private async onSelectedDriveFolderChanged(newVal: any, oldVal: any) {
    if (!this.selectedDriveFolder) { return; }
    this.$emit('updateSelectedDriveFolder', this.selectedDriveFolder);
  }

  @Watch('selectedFiles', { immediate: true, deep: true })
  private async onSelectedFilesChanged(newVal: any, oldVal: any) {
    if (!this.selectedFiles || this.selectedFiles.length === 0) { return; }
    this.$emit('updateSelectedFiles', this.selectedFilesMeta);
  }

}
